import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AppProvider, useApp } from './context/AppContext';
import SplashScreen from './components/SplashScreen';
import LanguageSelector from './components/LanguageSelector';
import GenderSelection from './components/GenderSelection';
import ProductList from './components/ProductList';
import ProductDetail from './components/ProductDetail';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import CheckoutResult from './components/CheckoutResult';
import Auth from './components/Auth';
import Account from './components/Account';
import Wishlist from './components/Wishlist';
import QRLogin from './components/QRLogin';
import VerifyEmail from './components/VerifyEmail';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminOrders from './components/admin/AdminOrders';
import AdminProducts from './components/admin/AdminProducts';
import AdminCustomers from './components/admin/AdminCustomers';
import AdminReviews from './components/admin/AdminReviews';
import Admin360Views from './components/admin/Admin360Views';
import AdminManagers from './components/admin/AdminManagers';

// Admin Suggestions Page Wrapper
const AdminSuggestionsPage = React.lazy(() => import('./components/admin/AdminSuggestionsPage'));
import ManagerLogin from './components/admin/ManagerLogin';
import ManagerDashboard from './components/admin/ManagerDashboard';
import FloatingLanguageSelector from './components/FloatingLanguageSelector';
import FAQPage from './components/pages/FAQPage';
import VerzendingPage from './components/pages/VerzendingPage';
import AlgemeneVoorwaardenPage from './components/pages/AlgemeneVoorwaardenPage';
import RuilenRetournerenPage from './components/pages/RuilenRetournerenPage';
import BetaalmethodesPage from './components/pages/BetaalmethodesPage';
import './App.css';

const CustomerRoutes = () => {
  const [showSplash, setShowSplash] = useState(true);
  const { language } = useApp();

  // Seed database on first load
  useEffect(() => {
    const seedDB = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        await fetch(`${API}/seed`, { method: 'POST' });
      } catch (error) {
        console.log('Seed skipped');
      }
    };
    seedDB();
  }, []);

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  // If no language selected, show language selector
  if (!language) {
    return <LanguageSelector />;
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<GenderSelection />} />
        <Route path="/products/:category" element={<ProductList />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/success" element={<CheckoutResult success={true} />} />
        <Route path="/checkout/cancel" element={<CheckoutResult success={false} />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<Auth mode="login" />} />
        <Route path="/register" element={<Auth mode="register" />} />
        
        {/* Account Routes */}
        <Route path="/account" element={<Account />} />
        <Route path="/account/:tab" element={<Account />} />
        <Route path="/account/wishlist" element={<Wishlist />} />
        
        {/* Content Pages */}
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/verzending" element={<VerzendingPage />} />
        <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaardenPage />} />
        <Route path="/ruilen-retourneren" element={<RuilenRetournerenPage />} />
        <Route path="/betaalmethodes" element={<BetaalmethodesPage />} />
        
        {/* QR Login Route */}
        <Route path="/qr-login/:sessionId" element={<QRLogin />} />
        
        {/* Email Verification Route */}
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
      
      {/* Floating Language Selector - visible on all customer pages */}
      <FloatingLanguageSelector />
    </>
  );
};

const AppContent = () => {
  const location = window.location.pathname;
  
  // Admin and Manager routes are completely separate - no splash, no language selector
  if (location.startsWith('/admin') || location.startsWith('/manager')) {
    return (
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/reset-password/:resetToken" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/reviews" element={<AdminReviews />} />
        <Route path="/admin/360-views" element={<Admin360Views />} />
        <Route path="/admin/customers" element={<AdminCustomers />} />
        <Route path="/admin/suggestions" element={<AdminSuggestionsPage />} />
        <Route path="/admin/managers" element={<AdminManagers />} />
        
        {/* Manager Routes */}
        <Route path="/manager/login" element={<ManagerLogin />} />
        <Route path="/manager/dashboard" element={<ManagerDashboard />} />
      </Routes>
    );
  }

  return <CustomerRoutes />;
};

function App() {
  return (
    <Router>
      <AppProvider>
        <AppContent />
        <Toaster 
          position="top-right"
          duration={2500}
          toastOptions={{
            style: {
              background: '#1C1917',
              border: '1px solid #2C1B18',
              color: '#F5F5F0',
            },
          }}
        />
      </AppProvider>
    </Router>
  );
}

export default App;
